// AcademicPlanningPage.js - Academic Planning Management
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../context/ToastContext';
import {
  Box, Typography, Paper, Stack, Button, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, CircularProgress, Alert, FormControl, InputLabel,
  Select, MenuItem, Autocomplete, Divider, List, ListItem, ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BookIcon from '@mui/icons-material/Book';
import { motion } from 'framer-motion';
import {
  fetchAcademicPlans,
  createAcademicPlan,
  updateAcademicPlan,
  deleteAcademicPlan,
  fetchAcademicTerms
} from '../slices/academicsSlice';
import { fetchClassSubjects } from '../slices/subjectsSlice';
import { fetchClasses } from '../slices/classesSlice';

const MotionPaper = motion(Paper);

const AcademicPlanningPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    school_class: '',
    subject: '',
    academic_term: ''
  });

  const { academicPlans, academicTerms, loading, saving } = useSelector((state) => ({
    academicPlans: state.academics.academicPlans || [],
    academicTerms: state.academics.academicTerms || [],
    loading: state.academics.loading,
    saving: state.academics.saving
  }));

  const { classes, subjects } = useSelector((state) => ({
    classes: state.classes.classes || [],
    subjects: state.subjects.subjects || []
  }));

  const [formData, setFormData] = useState({
    subject: '',
    school_class: '',
    academic_term: '',
    plan_name: '',
    description: '',
    exam_weight_total: 60.0,
    assignment_weight_total: 30.0,
    participation_weight: 10.0,
    planned_exams: [],
    planned_assignments: [],
    is_active: true,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadPlans();
  }, [filters]);

  const loadData = () => {
    dispatch(fetchAcademicTerms());
    dispatch(fetchClasses());
  };

  const loadPlans = () => {
    const params = {};
    if (filters.school_class) params.school_class = filters.school_class;
    if (filters.subject) params.subject = filters.subject;
    if (filters.academic_term) params.academic_term = filters.academic_term;
    dispatch(fetchAcademicPlans(params));
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        subject: plan.subject || plan.subject_id || '',
        school_class: plan.school_class || plan.school_class_id || '',
        academic_term: plan.academic_term || plan.academic_term_id || '',
        plan_name: plan.plan_name || '',
        description: plan.description || '',
        exam_weight_total: plan.exam_weight_total || 60.0,
        assignment_weight_total: plan.assignment_weight_total || 30.0,
        participation_weight: plan.participation_weight || 10.0,
        planned_exams: plan.planned_exams || [],
        planned_assignments: plan.planned_assignments || [],
        is_active: plan.is_active !== undefined ? plan.is_active : true,
        notes: plan.notes || ''
      });
    } else {
      setEditingPlan(null);
      setFormData({
        subject: '',
        school_class: '',
        academic_term: '',
        plan_name: '',
        description: '',
        exam_weight_total: 60.0,
        assignment_weight_total: 30.0,
        participation_weight: 10.0,
        planned_exams: [],
        planned_assignments: [],
        is_active: true,
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPlan(null);
  };

  const handleSavePlan = async () => {
    try {
      const planData = {
        subject: formData.subject,
        school_class: formData.school_class,
        academic_term: formData.academic_term,
        plan_name: formData.plan_name,
        description: formData.description || '',
        exam_weight_total: parseFloat(formData.exam_weight_total),
        assignment_weight_total: parseFloat(formData.assignment_weight_total),
        participation_weight: parseFloat(formData.participation_weight),
        planned_exams: formData.planned_exams,
        planned_assignments: formData.planned_assignments,
        is_active: formData.is_active,
        notes: formData.notes || ''
      };

      if (editingPlan) {
        await dispatch(updateAcademicPlan({ id: editingPlan.id, ...planData })).unwrap();
        showToast('✅ تم تحديث الخطة الدراسية بنجاح');
      } else {
        await dispatch(createAcademicPlan(planData)).unwrap();
        showToast('✅ تم إنشاء الخطة الدراسية بنجاح');
      }
      
      handleCloseDialog();
      loadPlans();
    } catch (error) {
      showToast('❌ فشل في حفظ الخطة الدراسية');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الخطة الدراسية؟')) {
      return;
    }
    
    try {
      await dispatch(deleteAcademicPlan(planId)).unwrap();
      showToast('✅ تم حذف الخطة الدراسية بنجاح');
      loadPlans();
    } catch (error) {
      showToast('❌ فشل في حذف الخطة الدراسية');
    }
  };

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
    setViewDialogOpen(true);
  };

  // Filter subjects by selected class
  const filteredSubjects = filters.school_class
    ? subjects.filter(s => s.school_class === filters.school_class || s.school_class_id === filters.school_class)
    : subjects;

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BookIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                التخطيط الأكاديمي
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                إنشاء وإدارة الخطط الدراسية وتوزيع الأوزان
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة خطة دراسية
          </Button>
        </MotionPaper>

        {/* Filters */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: '#1E1B4B',
              mb: 2,
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            تصفية الخطط
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={classes}
                getOptionLabel={(option) => option.name || ''}
                value={classes.find(c => c.id === filters.school_class) || null}
                onChange={(event, newValue) => {
                  setFilters({ ...filters, school_class: newValue?.id || '', subject: '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="الصف"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={filteredSubjects}
                getOptionLabel={(option) => option.name || ''}
                value={filteredSubjects.find(s => s.id === filters.subject) || null}
                onChange={(event, newValue) => {
                  setFilters({ ...filters, subject: newValue?.id || '' });
                }}
                disabled={!filters.school_class}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="المادة"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={academicTerms}
                getOptionLabel={(option) => option.name || ''}
                value={academicTerms.find(t => t.id === filters.academic_term) || null}
                onChange={(event, newValue) => {
                  setFilters({ ...filters, academic_term: newValue?.id || '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="الفصل الدراسي"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </MotionPaper>

        {/* Plans List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#4338CA' }} />
          </Box>
        ) : academicPlans.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              borderRadius: 3,
              bgcolor: '#E0F2FE',
              color: '#0284C7',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              fontWeight: 700,
              border: '1px solid #E5E7EB',
            }}
          >
            لا توجد خطط دراسية مسجلة
          </Alert>
        ) : (
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
            sx={{
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                    <TableCell sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>اسم الخطة</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>المادة</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الصف</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الفصل الدراسي</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الحالة</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>إجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {academicPlans.map((plan) => (
                    <TableRow
                      key={plan.id}
                      hover
                      sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}
                    >
                      <TableCell sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{plan.plan_name}</TableCell>
                      <TableCell sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{plan.subject_name || plan.subject?.name || '-'}</TableCell>
                      <TableCell sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{plan.school_class_name || plan.school_class?.name || '-'}</TableCell>
                      <TableCell sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{plan.academic_term_name || plan.academic_term?.name || '-'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={plan.is_active ? 'نشطة' : 'غير نشطة'}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontFamily: 'Cairo, Tajawal, sans-serif',
                            bgcolor: plan.is_active ? '#DCFCE7' : '#F1F5F9',
                            color: plan.is_active ? '#16A34A' : '#6B7280',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewPlan(plan)}
                          title="عرض التفاصيل"
                          sx={{ color: '#0284C7', '&:hover': { bgcolor: '#E0F2FE' } }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(plan)}
                          sx={{ color: '#4338CA', '&:hover': { bgcolor: '#EEF2FF' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePlan(plan.id)}
                          sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MotionPaper>
        )}

        {/* Add/Edit Plan Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, direction: 'rtl' }
          }}
        >
          <DialogTitle sx={{
            textAlign: 'center',
            fontWeight: 800,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            fontFamily: 'Cairo, Tajawal, sans-serif',
          }}>
            <Stack useFlexGap direction="row" spacing={1.5} alignItems="center" justifyContent="center">
              <BookIcon sx={{ color: '#fff' }} />
              <Typography variant="h6" fontWeight={800} sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                {editingPlan ? 'تعديل الخطة الدراسية' : 'إضافة خطة دراسية جديدة'}
              </Typography>
            </Stack>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="اسم الخطة"
                  value={formData.plan_name}
                  onChange={(e) => setFormData({...formData, plan_name: e.target.value})}
                  fullWidth
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={classes}
                  getOptionLabel={(option) => option.name || ''}
                  value={classes.find(c => c.id === formData.school_class) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, school_class: newValue?.id || '', subject: '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="الصف"
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={subjects.filter(s => s.school_class === formData.school_class || s.school_class_id === formData.school_class)}
                  getOptionLabel={(option) => option.name || ''}
                  value={subjects.find(s => s.id === formData.subject) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, subject: newValue?.id || '' });
                  }}
                  disabled={!formData.school_class}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="المادة"
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={academicTerms}
                  getOptionLabel={(option) => option.name || ''}
                  value={academicTerms.find(t => t.id === formData.academic_term) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, academic_term: newValue?.id || '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="الفصل الدراسي"
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="وصف الخطة (اختياري)"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2, borderColor: '#E5E7EB' }} />
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  توزيع الأوزان
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="وزن الاختبارات (%)"
                  type="number"
                  value={formData.exam_weight_total}
                  onChange={(e) => setFormData({...formData, exam_weight_total: e.target.value})}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="وزن الواجبات (%)"
                  type="number"
                  value={formData.assignment_weight_total}
                  onChange={(e) => setFormData({...formData, assignment_weight_total: e.target.value})}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="وزن المشاركة (%)"
                  type="number"
                  value={formData.participation_weight}
                  onChange={(e) => setFormData({...formData, participation_weight: e.target.value})}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#E0F2FE',
                    color: '#0284C7',
                    fontWeight: 700,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                >
                  المجموع: {(
                    parseFloat(formData.exam_weight_total || 0) +
                    parseFloat(formData.assignment_weight_total || 0) +
                    parseFloat(formData.participation_weight || 0)
                  ).toFixed(1)}%
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="ملاحظات (اختياري)"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleSavePlan}
              variant="contained"
              disabled={saving || !formData.plan_name || !formData.subject || !formData.school_class || !formData.academic_term}
              sx={{
                minWidth: 100,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                bgcolor: '#4338CA',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' }
              }}
            >
              {saving ? 'جاري الحفظ...' : editingPlan ? 'تحديث' : 'إضافة'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Plan Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, direction: 'rtl' }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            fontFamily: 'Cairo, Tajawal, sans-serif',
          }}>
            تفاصيل الخطة الدراسية: {selectedPlan?.plan_name}
          </DialogTitle>
          <DialogContent>
            {selectedPlan && (
              <Stack spacing={3} sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">المادة</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPlan.subject_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">الصف</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPlan.school_class_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">الفصل الدراسي</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPlan.academic_term_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">الحالة</Typography>
                    <Chip
                      label={selectedPlan.is_active ? 'نشطة' : 'غير نشطة'}
                      color={selectedPlan.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Grid>
                </Grid>

                {selectedPlan.description && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        الوصف
                      </Typography>
                      <Typography variant="body1">
                        {selectedPlan.description}
                      </Typography>
                    </Box>
                  </>
                )}

                <Divider />

                <Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    توزيع الأوزان
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: '#EEF2FF', textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الاختبارات</Typography>
                        <Typography variant="h6" fontWeight={800} sx={{ color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedPlan.exam_weight_total}%</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: '#FEF3C7', textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الواجبات</Typography>
                        <Typography variant="h6" fontWeight={800} sx={{ color: '#D97706', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedPlan.assignment_weight_total}%</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: '#DCFCE7', textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>المشاركة</Typography>
                        <Typography variant="h6" fontWeight={800} sx={{ color: '#16A34A', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedPlan.participation_weight}%</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {selectedPlan.planned_exams && selectedPlan.planned_exams.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        الاختبارات المخططة
                      </Typography>
                      <List>
                        {selectedPlan.planned_exams.map((exam, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={exam.name || 'اختبار'}
                              secondary={`التاريخ: ${exam.date || '-'} | الوزن: ${exam.weight || 1}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </>
                )}

                {selectedPlan.planned_assignments && selectedPlan.planned_assignments.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        الواجبات المخططة
                      </Typography>
                      <List>
                        {selectedPlan.planned_assignments.map((assignment, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={assignment.name || 'واجب'}
                              secondary={`تاريخ الاستحقاق: ${assignment.due_date || '-'} | الوزن: ${assignment.weight || 1}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => setViewDialogOpen(false)}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              إغلاق
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AcademicPlanningPage;
